import React from 'react';

import imageUrlBuilder from '@sanity/image-url';
import {Helmet} from 'react-helmet';
import {helmetJsonLdProp} from 'react-schemaorg';
import {Article} from 'schema-dts';
import {
  Flex,
  Box,
  StickySocialLinks,
  StickySocialLinksMobile,
  LabelledImageProps,
  SEOWithQuery,
  SEOProps,
} from 'src/components/shared';
import {colors} from 'src/styleguide/colors';
import {EditorialRichContent} from 'src/templates/editorial/EditorialContent';
import {seoTitlePrefix} from 'src/utils/copy';
import {sanityClient} from 'src/utils/sanity';
import styled from 'styled-components/macro';

import {SanityEditorial, Maybe, SanityLabelledImage} from 'types/generated';

import {EmailBannerSideBar} from '../../components/editorial';
import {InsightsNavigation} from '../insightsNavigation';
import {HeroHeader} from './HeroHeader';
import {MoreFromSeries} from './MoreFromSeries';
import {NextUpFeatured} from './NextUpFeatured';
import {PixelTrackingImage} from './PixelTrackingImage';
import {SupportingContent} from './SupportingContent';

interface EditorialProps {
  editorial: SanityEditorial;
  location: Location;
  href: string;
  isDraft?: boolean;
  next?: {
    title: string;
    slug: string;
    heroImage: SanityLabelledImage;
    content: string;
    article: Maybe<SanityEditorial>;
  };
  relatedArticles?: [Maybe<Pick<SanityEditorial, 'id' | 'title' | 'slug' | 'heroImage' | 'editorialCategory'>>];
}

const Content = styled(EditorialRichContent)``;
const Page = styled(Flex).attrs({
  alignItems: 'center',
  backgroundColor: colors.white,
  flexDirection: 'column',
})``;

const EditorialTemplate: React.FC<EditorialProps> = ({
  editorial,
  next,
  href,
  isDraft,
  relatedArticles,
}: EditorialProps) => {
  const pageRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    // Open links in new tab
    const links = pageRef?.current?.getElementsByTagName('a');

    if (links) {
      for (let i = 0; i < links.length; i++) {
        if (!links[i].getAttribute('href')?.includes('articles')) {
          //links[i].setAttribute('target', '_blank');
          links[i].setAttribute('rel', 'noopener noreferrer');
        }
      }
    }
  });

  if (!editorial) return null;
  let backgroundColor = '';
  const hasHeroImage = editorial.heroImage?.asset?.url ?? false;
  if (editorial.heroBackgroundColor && hasHeroImage) {
    switch (editorial.heroBackgroundColor.value) {
      case '#1E320B':
        backgroundColor = 'bg-green-dark';
        break;
      case '#003D3D':
        backgroundColor = 'bg-green-forest';
        break;
      case '#492426':
        backgroundColor = 'bg-plum';
        break;
      default:
        backgroundColor = 'bg-white';
    }
  }
  const noIndex = editorial.slug?.current?.includes('-uk', -3); // UK articles should be noIndex'd
  const hasNextArticle = typeof next?.title === 'string' && typeof next?.slug === 'string';
  const ldJsonScript = getHelmetJsonLdProp(editorial, href);
  return (
    <Page ref={pageRef}>
      <InsightsNavigation bgColor={backgroundColor} />
      <Helmet script={[ldJsonScript]} />
      <Box width="100%">
        <div className="px-gutter page-module-spacing relative">
          <div
            className={
              'absolute w-screen left-0 top-0 ' +
              backgroundColor +
              (editorial.sponsorship != null ? ' h-[500px] lg:h-[700px]' : ' h-[450px] lg:h-[650px]')
            }
          ></div>
          <div
            className={
              'wrapper-default grid grid-cols-4 md:grid-cols-12 gap-4 lg:gap-8 ' +
              (editorial.heroBackgroundColor && hasHeroImage && editorial.heroBackgroundColor.value != '#ffffff'
                ? 'text-white'
                : '')
            }
          >
            <StickySocialLinksMobile
              url={href}
              title={editorial.title ?? undefined}
              description={editorial.description ?? undefined}
            />
            {editorial._rawSponsorship && (
              <div className="col-span-full md:col-span-6 md:col-start-2">
                <div className="inline-flex bg-ivory py-2 px-5 items-center flex-wrap space-y-2">
                  <div className="flex items-center">
                    <span className="type-lota text-dark-gold mr-2 block">
                      {editorial._rawSponsorship.sponsoredText}
                    </span>
                  </div>

                  {editorial._rawSponsorship?.clientLogo?.asset ? (
                    <a
                      className="w-auto h-5 900px:h-7"
                      href={editorial._rawSponsorship.clientUrl ?? null}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <img
                        src={editorial._rawSponsorship.clientLogo?.asset?.url ?? ''}
                        alt={editorial._rawSponsorship.clientName + ' Logo'}
                        className="w-auto h-5 900px:h-7"
                      />
                    </a>
                  ) : null}
                </div>
              </div>
            )}
            <div className="col-span-1 sticky top-12 h-[230px] hidden md:block row-start-1 row-end-3">
              <StickySocialLinks
                url={href}
                title={editorial.title ?? undefined}
                description={editorial.description ?? undefined}
                classes={'flex-col md:mt-7 lg:mt-10 hidden space-y-3 md:flex '}
              />
            </div>
            <HeroHeader {...editorial} url={href} backgroundColor={backgroundColor} />

            <div data-mobile-social-icons className="md:hidden">
              <StickySocialLinks
                url={href}
                title={editorial.title ?? undefined}
                description={editorial.description ?? undefined}
                classes={'col-span-1 space-x-3 lg:mt-11 flex my-4'}
              />
            </div>

            <div
              className="col-span-full md:col-span-11 xl:col-span-8 md:col-start-2 xl:col-start-2 mt-1"
              data-js-article
            >
              {!!editorial.summary && (
                <div className="col-span-full md:col-start-2 mb-8 md:mb-6">
                  <span className="type-theta text-green-dark">Summary</span>
                  <ul className="type-p text-black list-outside list-disc ml-[1.1rem] marker:text-[#C6C6C6] mt-1 lg:mt-4">
                    <li className="!ml-0 md:ml-[.9rem]">{editorial.summary}</li>
                  </ul>
                </div>
              )}
              {!!editorial.content && <Content blocks={editorial.content} />}
            </div>
            <EmailBannerSideBar class={'col-span-full lg:col-span-3 lg:col-start-10'} id="newsletter-form" />
          </div>
          {relatedArticles && relatedArticles?.length > 0 ? <MoreFromSeries relatedArticles={relatedArticles} /> : null}
        </div>
      </Box>
      {noIndex ? <SEOWithQuery noIndex={true} /> : <SEOWithQuery {...getSEOMeta(editorial)} url={href} />}

      {!isDraft && editorial.sponsorship?.pixelTrackingImageUrl && (
        <PixelTrackingImage src={editorial.sponsorship.pixelTrackingImageUrl} />
      )}
      {!!editorial.supportingContent && (
        <SupportingContent supportingContent={editorial.supportingContent} nextUpFeaturedDefault={next} />
      )}

      {!editorial.supportingContent && hasNextArticle && (
        <NextUpFeatured heading={'Next Up'} cta={'Read More'} article={next.article} key={'default'} />
      )}
    </Page>
  );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getHelmetJsonLdProp(editorial: any, href: string) {
  return helmetJsonLdProp<Article>({
    '@context': 'https://schema.org',
    '@type': 'Article',
    url: href,
    keywords: editorial.tags,
    creator: [editorial.byline],
    headline: editorial.title || '',
    articleSection: editorial.category || '',
    thumbnailUrl: editorial.heroImage ? imageUrlBuilder(sanityClient).image(editorial.heroImage.asset).url() || '' : '',
    datePublished: editorial.publishDate ? new Date(editorial.publishDate).toISOString() : '',
    image: editorial.heroImage ? imageUrlBuilder(sanityClient).image(editorial.heroImage.asset).url() || '' : '',
  });
}

function getSEOMeta(editorial: SanityEditorial) {
  const heroImageUrl = editorial.heroImage ? editorial?.heroImage?.asset?.url : null;
  const seoProps = {
    title: `${seoTitlePrefix} ${editorial.title}`,
    ...(typeof editorial.description === 'string' && {description: editorial.description}),
    ...(typeof heroImageUrl === 'string' && {image: heroImageUrl}),
    ...(typeof editorial.byline === 'string' && {author: editorial.byline}),
    ...(typeof editorial.publishDate === 'string' && {publishDate: editorial.publishDate}),
    ...(Array.isArray(editorial.tags) && {tags: editorial.tags as string[]}),
  };
  return seoProps;
}

export default EditorialTemplate;
